import { useCallback, useEffect, useRef, useState } from 'react'
import { useRealtimeConnectionState } from '@/realtime/realtime-dispatcher'
import { useUploadSummary } from '@/upload/useUpload'
import { useUploadRealtime } from '@/upload/useUploadRealtime'
import {
	clearNotifications,
	dismissNotification,
	markNotificationsRead,
	useNotifications,
	useUnreadNotificationCount,
} from './notification-store'
import { StatusBar } from './StatusBar'

/**
 * Mounts the activity status bar and wires it to the notification store and the
 * realtime connection state.
 *
 * The bar is `sticky` at the bottom of the app shell's scroll container rather than
 * `fixed`: a fixed bar overlays the last row of whatever is on screen, which is the
 * most common complaint about this pattern. Sticky keeps it always visible while
 * still occupying layout space, so content ends above it.
 */
export function StatusBarHost() {
	const entries = useNotifications()
	const unreadCount = useUnreadNotificationCount()
	const connectionState = useRealtimeConnectionState()
	const uploadSummary = useUploadSummary()
	// Mounted here because this host is already rendered once, outside `<Outlet>`:
	// the upload queue is global, so tracking it from any page would stop the
	// moment the user navigated away from the page that started the upload.
	useUploadRealtime()
	const [expanded, setExpanded] = useState(false)
	const [now, setNow] = useState(() => Date.now())
	const containerRef = useRef<HTMLDivElement>(null)

	// Relative timestamps have to age on their own: the bar can sit untouched for
	// an hour, and without this a notification reads "just now" indefinitely. Once a
	// minute is the coarsest tick that keeps every label the component can render
	// ("just now", "2m ago", "3h ago") honest, and it costs one re-render a minute.
	useEffect(() => {
		const timer = setInterval(() => setNow(Date.now()), 60_000)
		return () => clearInterval(timer)
	}, [])

	const collapse = useCallback(() => setExpanded(false), [])

	const toggle = useCallback(() => {
		setExpanded((open) => {
			// Opening is the "I have looked at these" signal — a badge that cannot be
			// cleared is ignored within a day.
			if (!open) markNotificationsRead()
			return !open
		})
	}, [])

	// A non-modal popover: Escape and an outside click close it, but focus is never
	// trapped and the page is never dimmed. The panel is ambient, not a dialog.
	useEffect(() => {
		if (!expanded) return

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') collapse()
		}
		const onPointerDown = (event: PointerEvent) => {
			if (!containerRef.current?.contains(event.target as Node)) collapse()
		}

		document.addEventListener('keydown', onKeyDown)
		document.addEventListener('pointerdown', onPointerDown)
		return () => {
			document.removeEventListener('keydown', onKeyDown)
			document.removeEventListener('pointerdown', onPointerDown)
		}
	}, [expanded, collapse])

	return (
		<div ref={containerRef} className="sticky bottom-0 z-40 shrink-0">
			<StatusBar
				connectionState={connectionState}
				entries={entries}
				expanded={expanded}
				onClearAll={clearNotifications}
				onDismiss={dismissNotification}
				now={now}
				onToggleExpanded={toggle}
				unreadCount={unreadCount}
				uploadSummary={uploadSummary}
			/>
		</div>
	)
}
